import { DateTime } from "luxon";
import React, { useEffect, useState, useContext } from "react";
import { Form, Modal, Row, Col, Button, ToastContainer, Toast } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import debounce from 'debounce-promise';

import { homeOrgs, systemsAccess, accesories, ppe_ppc } from "../dropdowns";
import { searchUsers, updateNewStarterAPI } from "../graph";
import { loginRequest } from "../authConfig";
import { Context } from "../Context";


import "../styles/ModalViewDetailsRequest.css";

/**
 * Renders information about the user obtained from MS Graph
 * @param props 
 */
export const ModalStarterDetails = (props) => {
    const { instance, accounts } = useMsal();
    const [ request, setRequest ] = useState(props.request);
    const [ validated, setValidated ] = useState(false);
    const [ showToast, setShowToast ] = useState(false);
    const [ messageAPI, setMessageAPI ] = useState('');
    const [, setIsLoading] = useContext(Context);

    const isAdmin = () => ['Operational Technology', 'People and Culture'].includes(props.department);

    const capitalize = (text) => {
        const str = text.split('.');
        let result = '';
      
        str.forEach(element => {
          const lower = element.toLowerCase();
          result = `${result} ${element.charAt(0).toUpperCase()}${lower.slice(1)}`;
        });
      
        return result.slice(1);
    };

    const cleanNameFromIdentity = (identity) => {
        if (!identity) {
            return [];
        } 
        const [name] = identity.split('@');
        return {label: capitalize(name), value: identity};
    }
    
    useEffect(() => {
        let manager = ''

        if (!isAdmin()) {
            manager = accounts[0].username;
        }

        setRequest({...props.request, companyVehicle: 'No', homeOrg: 'HEB', manager});
        setValidated(false);
    }, [props.request])

    const getDateOnly = (fullDate) => {
        if (fullDate) {
            return DateTime.fromISO(fullDate).startOf('day').toISODate()
        }
        return '';
    }

    const customStyles = {
        control: base => ({
          ...base,
          height: 'auto',
          minHeight: 29,
          fontSize: 14,
        })
    };

    const customStylesRed = {
        control: base => ({
          ...base,
          height: 'auto',
          minHeight: 29,
          fontSize: 14,
          borderColor: 'red'
        })
    };

    const changeValue = (v) => {
        let value = v.currentTarget.value;

        if (['startDate', 'driverLicenseDate'].includes(v.currentTarget.id)) {
            value = DateTime.fromFormat(value, 'yyyy-MM-dd').toISO()
        }

        if (['systemsAccess', 'accesories', 'ppe_ppc'].includes(v.currentTarget.id)) {
            value = value.map(v => v.value);
        }

        if (['manager'].includes(v.currentTarget.id)) {
            value = value ? value.value : undefined;
        }

        if (v.currentTarget.id === 'companyVehicle' && value === 'No') {
            setRequest({...request, [v.currentTarget.id]: value, driverLicenseNumber: '', driverLicenseDate: ''});
        } else {
            setRequest({...request, [v.currentTarget.id]: value})
        }
    };

    const submitRequest = (event) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();

        if (!confirm('Are you sure about the details of the new starter?')) {
            return;
        }

        if (form.checkValidity() && request?.manager) {
            // Silently acquires an access token which is then attached to a request for MS Graph data
            instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0]
            }).then((response) => {
                setIsLoading(true);

                updateNewStarterAPI(response.accessToken, request)
                    .then(() => {
                        props.onNewStarterAdded();
                        props.onClose();
                    })
                    .catch(error => {
                        setMessageAPI(`Error, ${error}`);
                        setShowToast(true);
                        setValidated(false);
                    })
                    .finally (() => {
                        setIsLoading(false);
                    });
            });
        } else {
            setValidated(true);
        }
    };

    const getUsers = async (inputValue, exclude) => {
        try {
            // Silently acquires an access token which is then attached to a request for MS Graph data
            const response = await instance.acquireTokenSilent({...loginRequest, account: accounts[0]});
            const users = await searchUsers(response.accessToken, inputValue);
            return users.length
                ? users.filter(u => u.mail !== exclude).map((option) => ({
                    label: `${option.givenName} ${option.surname}`,
                    value: option.mail,
                }))
            : [];
        } catch (e) {
            return [];
        }
    };

    const debouncedLoadUsers = debounce((inputValue, exclude) => getUsers(inputValue, exclude), 300, {
      leading: true
    });

    return (
        <>
            <Modal
                className="modalDetailsRequest"
                show={props.show}
                onHide={props.onClose}
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Form noValidate validated={validated} onSubmit={submitRequest} >
                    <Modal.Header closeButton>
                        <Modal.Title>New starter details for {request?.position}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="mb-2">
                            <Form.Group as={Col} controlId="firstName">
                                <Form.Label>First Name</Form.Label>
                                <Form.Control required onChange={changeValue} value={request?.firstName || ''} />
                            </Form.Group>
                            <Form.Group as={Col} controlId="lastName">
                                <Form.Label>Last Name</Form.Label>
                                <Form.Control required onChange={changeValue} value={request?.lastName || ''} />
                            </Form.Group>
                        </Row>
                        <Row className="mb-2">
                            <Form.Group as={Col} controlId="startDate">
                                <Form.Label>Start date</Form.Label>
                                <Form.Control min={new Date().toISOString().split("T")[0]} type="date" required pattern="\d{2}/\d{2}/\d{4}" onChange={changeValue} value={getDateOnly(request?.startDate)} />
                            </Form.Group>
                            <Form.Group as={Col} controlId="manager">
                                <Form.Label>Manager</Form.Label>
                                <AsyncSelect
                                    isClearable
                                    isDisabled={!isAdmin()}
                                    cacheOptions
                                    classNamePrefix="select"
                                    loadOptions={debouncedLoadUsers}
                                    onChange={(v) => changeValue({currentTarget: { id: 'manager', value: v}})}
                                    placeholder={'Type a name'}
                                    value={cleanNameFromIdentity(request?.manager)}
                                    styles={validated && !request?.manager ? customStylesRed : customStyles}
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-2">
                            <Form.Group as={Col} controlId="homeOrg">
                                <Form.Label>Home Org</Form.Label>
                                <Form.Select name="homeOrg" required size="sm" aria-label="homeOrg" onChange={changeValue} value={request?.homeOrg || ''} >
                                    {homeOrgs.map(homeOrg => (
                                        <option key={homeOrg.value} value={homeOrg.value}>{homeOrg.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group as={Col} controlId="homeOrgManager">
                                <Form.Label>Home Org Manager</Form.Label>
                                <Form.Control onChange={changeValue} value={request?.homeOrgManager || ''} />
                            </Form.Group>
                            <Form.Group as={Col} sm={4} controlId="companyVehicle">
                                <Form.Label>Company Vehicle?</Form.Label>
                                <Form.Select name="companyVehicleOptions" required size="sm" aria-label="companyVehicle" onChange={changeValue} value={request?.companyVehicle || ''} >
                                    {['Yes', 'No'].map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Row>
                        <Row className="mb-2">
                            { request?.companyVehicle === 'Yes' && (
                                <>
                                    <Form.Group as={Col} controlId="driverLicenseNumber">
                                        <Form.Label>Driver License Number</Form.Label>
                                        <Form.Control required={request?.companyVehicle === 'Yes'} onChange={changeValue} value={request?.driverLicenseNumber || ''} />
                                    </Form.Group>
                                    <Form.Group as={Col} controlId="driverLicenseDate">
                                        <Form.Label>Driver License Expire</Form.Label>
                                        <Form.Control min={new Date().toISOString().split("T")[0]} type="date" required={request?.companyVehicle === 'Yes'} pattern="\d{2}/\d{2}/\d{4}" onChange={changeValue} value={getDateOnly(request?.driverLicenseDate)} />
                                    </Form.Group>
                                </>
                            )}
                        </Row>
                        <Row className="mb-2">
                            <Form.Group as={Col} controlId="systemsAccess">
                                <Form.Label>Systems Access</Form.Label>
                                <CreatableSelect
                                    isMulti
                                    value={request?.systemsAccess ? request?.systemsAccess.map(v => ({label:v, value:v})) : []}
                                    onChange={(v) => changeValue({currentTarget: { id: 'systemsAccess', value: v}})}
                                    name="systemsAccessOptions"
                                    options={systemsAccess}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    styles={customStyles}
                                    menuPlacement="top"
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-2">
                            <Form.Group as={Col} controlId="accesories">
                                <Form.Label>Accesories</Form.Label>
                                <CreatableSelect
                                    isMulti
                                    value={request?.accesories ? request?.accesories.map(v => ({label:v, value:v})) : []}
                                    onChange={(v) => changeValue({currentTarget: { id: 'accesories', value: v}})}
                                    name="accesoriesOptions"
                                    options={accesories}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    styles={customStyles}
                                    menuPlacement="top"
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-2">
                            <Form.Group as={Col} controlId="ppe_ppc">
                                <Form.Label>PPE / PPC</Form.Label>
                                <CreatableSelect
                                    isMulti
                                    value={request?.ppe_ppc ? request?.ppe_ppc.map(v => ({label:v, value:v})) : []}
                                    onChange={(v) => changeValue({currentTarget: { id: 'ppe_ppc', value: v}})}
                                    name="ppe_ppcOptions"
                                    options={ppe_ppc}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    styles={customStyles}
                                    menuPlacement="top"
                                />
                            </Form.Group>
                        </Row>
                        <Button className="updateUser" variant="success" type="submit">Add details</Button>
                    </Modal.Body>
                </Form>
                <ToastContainer className="position-absolute p-3 top-50 start-50 translate-middle" >
                    <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} bg={messageAPI.substr(0, 6) !== 'Error,' ? 'light' : 'warning'} autohide={messageAPI.substr(0, 6) !== 'Error,'} >
                        <Toast.Header>
                            {messageAPI.substr(0, 6) === 'Error,' && <strong>Something went wrong</strong>}
                        </Toast.Header>
                        <Toast.Body>{messageAPI}</Toast.Body>
                    </Toast>
                </ToastContainer>
            </Modal>
        </>
    );
};